import { useSetToast } from "@app/components/Toast";
import { questionnaireLocaleAtom } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/ctx";
import { SortableList } from "@app/shared/components/SortableList";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { type UniqueIdentifier } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { ResponseStatus } from "@generated/client/graphql";
import { useAtomValue } from "jotai";
import { type FC, useEffect, useMemo, useState } from "react";
import {
    useTemplateQuestions,
    useUpdateSupplierTemplateQuestionsOrder,
} from "../../data";
import { GroupSidebar } from "./GroupSidebar";
import { useSupplierQuestionsSortableListTranslations } from "./i18n";

export const QuestionSortableList: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
    const currentLocale = useAtomValue(questionnaireLocaleAtom);
    const { templateQuestions, isPending } = useTemplateQuestions();

    const { updateQuestionsOrder, isPending: isUpdateQuestionsOrderPending } =
        useUpdateSupplierTemplateQuestionsOrder();

    const { setToastError } = useSetToast();

    const { t, keys } = useSupplierQuestionsSortableListTranslations();

    const [activeGroupId, setActiveGroupId] = useState<string | undefined>(
        undefined,
    );

    const questions = useMemo(() => {
        return templateQuestions.list.map((question) => ({
            id: question.id,
            name: question.nameMultiLocale?.[currentLocale ?? "en"],
            groupId: question.group?.id,
            groupName: question.group?.name?.[currentLocale ?? "en"],
        }));
    }, [templateQuestions.list, currentLocale]);

    const questionsGroups = useMemo(() => {
        const groups: { id: string; name: string }[] = [];

        questions.forEach((question) => {
            const groupId = question.groupId;
            const groupName =
                question.groupName || t(keys.group_not_translated);
            if (groupId && !groups.some((group) => group.id === groupId)) {
                groups.push({
                    id: groupId,
                    name: groupName,
                });
            }
        });
        groups.push({
            id: "no-group",
            name: t(keys.no_group),
        });
        return groups;
    }, [questions, currentLocale]);

    const [sortedQuestions, setSortedQuestions] = useState(questions);

    const displayedQuestions = useMemo(() => {
        if (!activeGroupId) {
            return sortedQuestions;
        }
        return sortedQuestions.filter((question) => {
            return (
                question.groupId === activeGroupId ||
                (activeGroupId === "no-group" && !question.groupId)
            );
        });
    }, [sortedQuestions, activeGroupId]);

    const handleItemsChange = (
        activeItemId: UniqueIdentifier,
        overItemId: UniqueIdentifier | undefined,
    ) => {
        if (activeItemId !== overItemId) {
            setSortedQuestions((items) => {
                const oldIndex = items.findIndex(
                    (item) => item.id === activeItemId,
                );
                const newIndex = items.findIndex(
                    (item) => item.id === overItemId,
                );

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setSortedQuestions(questions);
        }
        onOpenChange(open);
    };

    const handleSave = () => {
        const finalQuestions = sortedQuestions.map((question, idx) => ({
            questionId: question.id,
            order: idx,
        }));
        const questionsToUpdate = finalQuestions.filter(
            (question) =>
                question.order !==
                templateQuestions.list.find((q) => q.id === question.questionId)
                    ?.order,
        );

        updateQuestionsOrder(questionsToUpdate, {
            onSettled(data) {
                if (
                    data?.updateSupplierTemplateQuestionsOrder?.status ===
                    ResponseStatus.Success
                ) {
                    onOpenChange(false);
                    return;
                }
                setToastError(t(keys.toast_error_message));
            },
        });
    };

    useEffect(() => {
        if (isPending) {
            return;
        }
        setSortedQuestions(questions);
    }, [questions, isPending]);

    useEffect(() => {
        if (questionsGroups.length > 0) {
            setActiveGroupId(questionsGroups[0].id);
        }
    }, [questionsGroups]);

    return (
        <Modal open={open} onOpenChange={handleOpenChange}>
            <Modal.Overlay centerContent scrollable>
                <Modal.Content className="overflow-hidden">
                    <Modal.Header>
                        <Modal.Title>
                            {t(keys.sort_questions_modal_title)}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isPending ? (
                            <FlexCol gap="2" w="full">
                                {Array.from({ length: 10 }).map((_, index) => (
                                    <SortableList.SortableItemSkeleton
                                        key={index}
                                    />
                                ))}
                            </FlexCol>
                        ) : (
                            <FlexRow
                                h="full"
                                w="full"
                                gap="2"
                                className="max-h-[60vh] overflow-y-scroll scroll-auto"
                            >
                                {questionsGroups.length > 1 && (
                                    <GroupSidebar
                                        activeGroupId={activeGroupId}
                                        groups={questionsGroups}
                                        onActiveGroupIdChange={setActiveGroupId}
                                    />
                                )}
                                <SortableList
                                    items={displayedQuestions}
                                    onItemsChange={handleItemsChange}
                                >
                                    <FlexCol gap="2" w="full">
                                        {displayedQuestions.map((question) => (
                                            <SortableList.SortableItem
                                                key={question.id}
                                                id={question.id}
                                                className={
                                                    question.name
                                                        ? ""
                                                        : "text-secondary"
                                                }
                                            >
                                                {question.name ||
                                                    t(keys.no_name)}
                                            </SortableList.SortableItem>
                                        ))}
                                    </FlexCol>
                                </SortableList>
                            </FlexRow>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Modal.CancelCTA>{t(keys.cancel)}</Modal.CancelCTA>
                        <Modal.ConfirmCTA
                            preventClose
                            onClick={handleSave}
                            loading={isUpdateQuestionsOrderPending}
                        >
                            {t(keys.save)}
                        </Modal.ConfirmCTA>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};
